import htmx from "htmx.org";
import { uiBreakpoints } from "../utilities";

let viewportUpdatePending = false;

/**
 * [data-offcanvas] if present, the target element is treated as an off-canvas menu.
 * @param content
 */
export function bindOffcanvas(content?: Element | Document | null | undefined) {
    if (!content) {
        content = document;
    }
    content.querySelectorAll<HTMLElement>('[data-offcanvas]').forEach(offcanvas => {
        buildOffCanvasElements(offcanvas);
    });
}

function buildOffCanvasElements(elem: HTMLElement) {
    const id = elem.id || Math.random().toString(36).substring(2, 15);
    if (!elem.nextElementSibling || !elem.nextElementSibling.classList.contains('off-canvas-backdrop')) {
        const offcanvasBackdrop = document.createElement("div");
        offcanvasBackdrop.id = `offcanvas-backdrop-${id}`;
        offcanvasBackdrop.classList.add('off-canvas-backdrop');
        elem.after(offcanvasBackdrop);
    }
    elem.id ??= `offcanvas-${id}`;
    elem.className = `${elem.className} off-canvas`.trim();
}

function adjustOffCanvasPosition(elem: HTMLElement) {
    elem.classList.remove('xs:right-0');
    elem.classList.remove('xs:right-auto');

    const elemRect = elem.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (elemRect.right >= viewportWidth) {
        elem.classList.add('xs:right-0');
    }
    else {
        elem.classList.add('xs:right-auto');
    }
}

function windowResize() {
    document.querySelectorAll<HTMLElement>('[data-offcanvas]').forEach(offcanvas => adjustOffCanvasPosition(offcanvas));
    scheduleSearchFilterViewportUpdate();
}

function updateSearchFilterViewport(elem: HTMLElement) {
    if (!elem.hasAttribute('data-offcanvas-search')) {
        return;
    }

    const viewport = window.visualViewport;
    if (window.innerWidth >= uiBreakpoints.extraSmall || elem.dataset.expanded !== 'true' || !viewport) {
        elem.style.removeProperty('bottom');
        elem.style.removeProperty('max-height');
        elem.removeAttribute('data-compact-viewport');
        return;
    }

    // Keep this maximum in sync with .off-canvas in usaj-theme.css.
    const maxPanelHeightPx = 480;
    const minResultsSpacePx = 100;

    // Safari can pan and resize the visual viewport without resizing the layout viewport.
    const availableHeight = Math.min(maxPanelHeightPx, viewport.height);
    elem.style.bottom = `${Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)}px`;
    elem.style.maxHeight = `${availableHeight}px`;

    const header = elem.querySelector<HTMLElement>('[data-offcanvas-header]');
    const footer = elem.querySelector<HTMLElement>('[data-offcanvas-footer]');
    // On very short screens, scroll the whole panel rather than collapsing the results.
    const controlsHeight = (header?.offsetHeight ?? 0) + (footer?.offsetHeight ?? 0);
    const footerMargin = footer ? parseFloat(getComputedStyle(footer).marginTop) || 0 : 0;
    elem.toggleAttribute('data-compact-viewport', availableHeight < controlsHeight + footerMargin + minResultsSpacePx);
}

function scheduleSearchFilterViewportUpdate() {
    if (viewportUpdatePending) {
        return;
    }
    viewportUpdatePending = true;
    requestAnimationFrame(() => {
        viewportUpdatePending = false;
        document.querySelectorAll<HTMLElement>('[data-offcanvas-search]').forEach(updateSearchFilterViewport);
    });
}

document.addEventListener('DOMContentLoaded', () => bindOffcanvas(document));

document.addEventListener('toggle:show', evt => {
    let target: HTMLElement | null = evt.target as HTMLElement;
    if (!target || evt.target === document) {
        return;
    }
    if (target.hasAttribute('data-target')) {
        target = document.querySelector<HTMLElement>(`#${target.getAttribute('data-target')}`);
        if (!target) {
            return;
        }
    }
    if (target.matches('[data-offcanvas]')) {
        requestAnimationFrame(() => {
            adjustOffCanvasPosition(target);
            updateSearchFilterViewport(target);
        });
    }
});

document.addEventListener('toggle:dismiss', evt => {
    if (evt.target instanceof HTMLElement) {
        updateSearchFilterViewport(evt.target);
    }
});

window.addEventListener('resize', windowResize);
window.visualViewport?.addEventListener('resize', scheduleSearchFilterViewportUpdate, { passive: true });
window.visualViewport?.addEventListener('scroll', scheduleSearchFilterViewportUpdate, { passive: true });
htmx.onLoad((content) => bindOffcanvas(content as HTMLElement));
